import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import dayjs from "dayjs";
import { isNil } from "lodash";
import type React from "react";
import {
  type AdhocPreviewResultQuery,
  ScannedOrderSource,
  useScannedOrderResultAdminDetailQuery,
} from "../../../generated/graphql";
import AdhocDecisionPanel from "../../ingestion-adhoc-preview/adhoc-decision-panel";
import AdhocUsageLine from "../../ingestion-adhoc-preview/adhoc-usage-line";
import CollapsibleJson from "../../ingestion-test-runs/collapsible-json";
import LogExemplarPanel from "./log-exemplar-panel";

/**
 * The apply-plan shape rendered by {@link AdhocDecisionPanel}. The backend
 * returns the SAME `AdhocApplyPlan` GraphQL type the ad-hoc preview and
 * test-run tools consume, so the cast bridges the per-query generated type
 * names, not different shapes.
 */
type ApplyPlan = NonNullable<
  AdhocPreviewResultQuery["adhocPreviewResult"]["applyPlan"]
>;

const styles = {
  headerActions: { ml: "auto" },
  mono: { fontFamily: "monospace" },
} satisfies Record<string, SxProps<Theme>>;

/** Raw `<object>` element style (not a MUI sx target). */
const documentStyle: React.CSSProperties = {
  height: "72vh",
  width: "100%",
};

/**
 * Drill-down for one doc-ingestion log card: the source document beside the
 * extraction's provenance — the executed apply-decision plan, cost/usage,
 * extracted fields, and the exemplars that informed the extraction — reusing
 * the ad-hoc preview / test-run debug panels so all three tools render these
 * identically. Detail beyond the document itself exists only for V3
 * (document-extraction pipeline) scans; legacy scans show the document and a
 * note.
 */
const LogDetailDialog = ({
  resultUuid,
  onClose,
}: {
  readonly resultUuid: string;
  readonly onClose: () => void;
}) => {
  const { data, loading, error } = useScannedOrderResultAdminDetailQuery({
    variables: { uuid: resultUuid },
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  });
  const result = data?.scannedOrderResultForAdmin;
  const detail = result?.extractionDetail;

  // Wall-clock ingestion latency: from the email's arrival when the scan came
  // in over email integration (the operator-meaningful clock), else from the
  // ScannedOrder row's creation, to this result's creation.
  const processingStart =
    result?.scannedOrder.source === ScannedOrderSource.EmailIntegration
      ? ((result.scannedOrder.ingestedOutlookEmailDetails?.receivedAt ??
          result.scannedOrder.createdAt) as string)
      : (result?.scannedOrder.createdAt as string | undefined);
  const processingSeconds = isNil(result)
    ? null
    : dayjs(result.createdAt as string).diff(processingStart, "seconds");

  return (
    <Dialog fullWidth open maxWidth="xl" onClose={onClose}>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="h6">
            {result?.company.name ?? "Scan log"}
          </Typography>
          {!isNil(result) && (
            <>
              <Chip size="small" variant="outlined" label={result.status} />
              {!isNil(result.scannedOrder.documentIngestionPipeline) && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={result.scannedOrder.documentIngestionPipeline}
                />
              )}
              <Typography
                variant="body2"
                color="text.secondary"
                sx={styles.mono}
              >
                {result.uuid}
              </Typography>
            </>
          )}
          <Stack direction="row" spacing={1} sx={styles.headerActions}>
            {!isNil(result) && !isNil(result.preSignedGetUrl) && (
              <Button
                size="small"
                onClick={() => {
                  window.open(result.preSignedGetUrl ?? undefined, "_blank");
                }}
              >
                View original
              </Button>
            )}
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent>
        {loading && isNil(result) && (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={18} />
            <Typography>Loading scan detail…</Typography>
          </Stack>
        )}
        {!isNil(error) && isNil(result) && (
          <Alert severity="error">Failed to load: {error.message}</Alert>
        )}
        {!loading && isNil(result) && isNil(error) && (
          <Typography>Scan log not found.</Typography>
        )}
        {!isNil(result) && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <object
                data={result.preSignedGetUrl ?? undefined}
                style={documentStyle}
              >
                <Typography variant="body2" color="text.secondary">
                  No document preview available.
                </Typography>
              </object>
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack spacing={2}>
                {!isNil(result.failureReason) && (
                  <Alert severity="error">{result.failureReason}</Alert>
                )}
                <Stack direction="row" spacing={2} flexWrap="wrap">
                  <Typography variant="body2" color="text.secondary">
                    pages {result.pageStart}–{result.pageEnd}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    created{" "}
                    {dayjs(result.createdAt as string).format("M/D H:mm:ss")}
                  </Typography>
                  {!isNil(processingSeconds) && (
                    <Typography variant="body2" color="text.secondary">
                      processed in {processingSeconds}s
                    </Typography>
                  )}
                </Stack>
                {isNil(result.applyPlan) ? (
                  <Alert severity="info">
                    No decision plan recorded — this scan predates the
                    document-extraction pipeline or failed before planning.
                  </Alert>
                ) : (
                  <AdhocDecisionPanel
                    applyPlan={result.applyPlan as ApplyPlan}
                  />
                )}
                <AdhocUsageLine usage={detail?.usage as unknown} />
                <CollapsibleJson
                  defaultExpanded
                  title="Extracted fields"
                  value={detail?.extractedFields as unknown}
                />
                <LogExemplarPanel exemplars={result.exemplars} />
                {/* The resolved panel above is the readable view; the raw marker
                    stays available because it is what DE actually echoed —
                    including any entry the panel dropped as malformed. */}
                <CollapsibleJson
                  title="Exemplar marker — raw provenance as echoed by extraction"
                  value={detail?.exemplarMarker as unknown}
                />
                {!isNil(detail) && (
                  <Stack spacing={0.5}>
                    {!isNil(detail.correlationId) && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={styles.mono}
                      >
                        correlation {detail.correlationId}
                      </Typography>
                    )}
                    {!isNil(detail.pipelineVersion) && (
                      <Typography variant="body2" color="text.secondary">
                        pipeline {detail.pipelineVersion}
                        {isNil(detail.taxonomyVersion)
                          ? ""
                          : ` · taxonomy ${detail.taxonomyVersion}`}
                      </Typography>
                    )}
                  </Stack>
                )}
              </Stack>
            </Grid>
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LogDetailDialog;
